import classNames from 'classnames'
import React from 'react'

import { type UserGroupName } from '../api/interfaces/sconnStudyData'
import { useSurvey } from '../module/survey/hook'

import FormContent from './surveyFormContentLayout'

const ICON_BASE = `${process.env.PUBLIC_URL}/img/learning-keyword`

type LearnerGroupOption = {
  id: UserGroupName
  label: string
  description?: string
  iconSrc: string
  /** 학년 선택이 필요한 그룹만 보유 (초등 1~6, 중·고 1~3) */
  grades?: number[]
}

const LEARNER_GROUP_OPTIONS: LearnerGroupOption[] = [
  {
    id: 'university',
    label: '대학생',
    iconSrc: `${ICON_BASE}/ic_colleage_student.svg`,
  },
  {
    id: 'repeat_applicant',
    label: 'N수생/반수생',
    iconSrc: `${ICON_BASE}/ic_retake_student.svg`,
  },
  {
    id: 'general',
    label: '일반인',
    description: '자격증, 공무원, 취업',
    iconSrc: `${ICON_BASE}/ic_general_adult.svg`,
  },
  {
    id: 'high_school',
    label: '고등학생',
    iconSrc: `${ICON_BASE}/ic_high_school_student.svg`,
    grades: [1, 2, 3],
  },
  {
    id: 'middle_school',
    label: '중학생',
    iconSrc: `${ICON_BASE}/ic_middle_school_student.svg`,
    grades: [1, 2, 3],
  },
  {
    id: 'elementary',
    label: '초등학생',
    iconSrc: `${ICON_BASE}/ic_elementary_school_student.svg`,
    grades: [1, 2, 3, 4, 5, 6],
  },
]

function LearnerGroupCard({
  option,
  isSelected,
  selectedGrade,
  onSelect,
  onSelectGrade,
}: {
  option: LearnerGroupOption
  isSelected: boolean
  selectedGrade: number | null
  onSelect: () => void
  onSelectGrade: (grade: number) => void
}) {
  const isGradeSelectionVisible = isSelected && option.grades !== undefined

  return (
    <div
      className={classNames(
        'relative flex w-[160px] shrink-0 flex-col items-center rounded-[12px] border bg-white px-[12px] text-center',
        isGradeSelectionVisible
          ? 'justify-start pt-[36px] pb-[12px]'
          : 'h-[180px] justify-center py-[36px]',
        isSelected ? 'border-button-primary-blue' : 'border-common-line',
      )}
    >
      <button
        type='button'
        onClick={onSelect}
        aria-pressed={isSelected}
        className={classNames(
          'flex w-full flex-col items-center gap-[8px]',
          !isGradeSelectionVisible && 'flex-1 justify-center',
        )}
      >
        <div className='flex h-[68px] w-full shrink-0 items-center justify-center'>
          <img src={option.iconSrc} alt='' aria-hidden='true' className='max-h-full shrink-0' />
        </div>
        <div className='flex shrink-0 flex-col items-center gap-[2px]'>
          <span
            className={classNames(
              'text-[15px]',
              isSelected ? 'font-medium text-text-blue-primary' : 'text-text-black-primary',
            )}
          >
            {option.label}
          </span>
          {option.description && (
            <span
              className={classNames(
                'text-[12px]',
                isSelected ? 'text-text-blue-primary' : 'text-text-black-secondary',
              )}
            >
              {option.description}
            </span>
          )}
        </div>
      </button>

      {isGradeSelectionVisible && (
        <div className='flex w-full flex-col gap-[8px] pt-[24px]'>
          {option.grades?.map((grade) => {
            const isGradeSelected = selectedGrade === grade

            return (
              <button
                key={grade}
                type='button'
                aria-pressed={isGradeSelected}
                onClick={() => {
                  onSelectGrade(grade)
                }}
                className={classNames(
                  'flex h-[44px] w-full items-center justify-center rounded-full border text-[14px]',
                  isGradeSelected
                    ? 'border-button-primary bg-button-primary font-medium text-button-font'
                    : 'border-sub-border bg-white text-text-black-primary',
                )}
              >
                {grade}학년
              </button>
            )
          })}
        </div>
      )}

      {isSelected && (
        <span className='absolute -top-[4px] right-[8px] flex h-[24px] min-w-[24px] items-center justify-center rounded-full bg-button-primary-blue px-[8px] text-[12px] text-white'>
          ✓
        </span>
      )}
    </div>
  )
}

// V2 그룹 선택 step (마지막 스텝): 그룹 카드 가로 스크롤, 초·중·고는 학년까지 선택해야 제출 가능
export default function LearnerGroupSelect() {
  const { selectedUserGroup, selectedUserGroupGrade, setSelectedUserGroup } = useSurvey()

  const selectedOption = LEARNER_GROUP_OPTIONS.find((option) => option.id === selectedUserGroup)
  const needsGrade = selectedOption?.grades !== undefined
  const isComplete =
    selectedUserGroup !== null && (!needsGrade || selectedUserGroupGrade !== null)

  return (
    <FormContent>
      <FormContent.Grow className='mb-10'>
        <FormContent.Question
          title='마지막 단계예요!'
          description='아래에서 나에게 맞는 그룹을 선택해주세요. 더 잘 맞는 학습 콘텐츠와 경험을 준비할게요.'
        />

        <div className='mt-[30px] overflow-x-auto pt-[6px] pb-[12px]'>
          <div className='flex items-start gap-[8px]'>
            {LEARNER_GROUP_OPTIONS.map((option) => (
              <LearnerGroupCard
                key={option.id}
                option={option}
                isSelected={selectedUserGroup === option.id}
                selectedGrade={selectedUserGroup === option.id ? selectedUserGroupGrade : null}
                onSelect={() => {
                  // 그룹을 바꾸면 학년 선택은 초기화
                  setSelectedUserGroup(option.id, null)
                }}
                onSelectGrade={(grade) => {
                  setSelectedUserGroup(option.id, grade)
                }}
              />
            ))}
          </div>
        </div>
      </FormContent.Grow>
      <FormContent.ProcessButton disabled={!isComplete} onNextClick={() => {}} />
    </FormContent>
  )
}
